// Tareas + colas por rol (VLT-338 · Blueprint §22/VLT-281). Una tarea está
// asignada a una PERSONA o espera en la COLA DE UN ROL: cualquiera del rol la
// reclama. El workflow también las genera (source 'workflow'). RLS: staff de
// la org completa; los clientes no ven tareas internas.
import { supabase, hasBackend } from '@/lib/supabase';
import type { Role } from '@/types/domain';
import { loadFailed } from './loadError';
import { sessionUser } from '@/lib/sessionUser';

export type TaskStatus = 'open' | 'in_progress' | 'blocked' | 'done' | 'cancelled';
export type TaskPriority = 'low' | 'normal' | 'high' | 'urgent';
export type QueueRole = 'admin' | 'tax_preparer' | 'notary' | 'support';

export interface Task {
  id: string;
  title: string;
  details?: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  assigneeId?: string | null;
  assigneeName?: string | null;
  queueRole?: QueueRole | null;
  dueDate?: string | null;
  clientId?: string | null;
  clientName?: string | null;
  source: 'manual' | 'workflow';
  createdAt: string;
  /** VLT-281: cuándo escaló por plazo vencido (lo escribe el sistema). */
  escalatedAt?: string | null;
}

export interface NewTaskInput {
  title: string;
  details?: string;
  priority: TaskPriority;
  queueRole?: QueueRole;
  assigneeId?: string;
  dueDate?: string;
  clientId?: string;
  source?: 'manual' | 'workflow';
}

export const OPEN_STATUSES: TaskStatus[] = ['open', 'in_progress', 'blocked'];

/** ¿Vencida? (fecha límite anterior a hoy y aún abierta). */
export function isOverdue(t: Pick<Task, 'dueDate' | 'status'>, today = new Date()): boolean {
  if (!t.dueDate || !OPEN_STATUSES.includes(t.status)) return false;
  return t.dueDate < today.toISOString().slice(0, 10);
}

/** Ordena una cola: urgente→baja, y dentro, vencidas primero y por fecha. */
export function sortQueue<T extends Pick<Task, 'priority' | 'dueDate' | 'status'>>(tasks: T[], today = new Date()): T[] {
  const rank: Record<TaskPriority, number> = { urgent: 0, high: 1, normal: 2, low: 3 };
  return [...tasks].sort((a, b) => {
    const pr = rank[a.priority] - rank[b.priority];
    if (pr !== 0) return pr;
    const ov = Number(isOverdue(b, today)) - Number(isOverdue(a, today));
    if (ov !== 0) return ov;
    return (a.dueDate ?? '9999') < (b.dueDate ?? '9999') ? -1 : 1;
  });
}

/** Vista de trabajo §22: mías + la cola de mi rol (sin dueño aún). */
export function myWork<T extends Pick<Task, 'assigneeId' | 'queueRole' | 'status'>>(
  tasks: T[], myId: string, myRole: Role,
): { mine: T[]; queue: T[] } {
  const open = tasks.filter((t) => OPEN_STATUSES.includes(t.status));
  return {
    mine: open.filter((t) => t.assigneeId === myId),
    queue: open.filter((t) => !t.assigneeId && t.queueRole === myRole),
  };
}

export interface TasksService {
  list(): Promise<Task[]>;
  create(input: NewTaskInput): Promise<boolean>;
  setStatus(id: string, status: TaskStatus): Promise<boolean>;
  /** Reclamar de la cola del rol: la tarea pasa a la persona. */
  claim(id: string, profileId: string): Promise<boolean>;
}

// ─── Implementación real (Supabase, RLS staff por org) ──────────────────────

interface Row {
  id: string; title: string; details: string | null; status: TaskStatus; priority: TaskPriority;
  assignee_id: string | null; queue_role: QueueRole | null; due_date: string | null;
  escalated_at: string | null;
  client_id: string | null; source: 'manual' | 'workflow'; created_at: string;
  assignee: { full_name: string } | null;
  client: { first_name: string; last_name: string } | null;
}

const SELECT = 'id, title, details, status, priority, assignee_id, queue_role, due_date, client_id, source, created_at, escalated_at, assignee:profiles!assignee_id(full_name), client:clients(first_name, last_name)';

const realTasks: TasksService = {
  async list() {
    const { data, error } = await supabase.from('tasks').select(SELECT)
      .order('created_at', { ascending: false }).limit(200);
    if (error) loadFailed('tasks.list', error);
    return ((data as unknown as Row[]) ?? []).map((r) => ({
      id: r.id, title: r.title, details: r.details, status: r.status, priority: r.priority,
      assigneeId: r.assignee_id, assigneeName: r.assignee?.full_name ?? null,
      queueRole: r.queue_role, dueDate: r.due_date, clientId: r.client_id,
      clientName: r.client ? `${r.client.first_name} ${r.client.last_name}` : null,
      source: r.source, createdAt: r.created_at, escalatedAt: r.escalated_at,
    }));
  },

  async create(input) {
    const { data: auth } = await sessionUser();
    if (!auth.user) return false;
    const { data: prof } = await supabase.from('profiles').select('organization_id').eq('id', auth.user.id).maybeSingle();
    const orgId = (prof as { organization_id: string } | null)?.organization_id;
    if (!orgId) return false;
    const { error } = await supabase.from('tasks').insert({
      organization_id: orgId, title: input.title.trim(), details: input.details?.trim() || null,
      priority: input.priority, queue_role: input.queueRole ?? null,
      assignee_id: input.assigneeId ?? null, due_date: input.dueDate || null,
      client_id: input.clientId ?? null, source: input.source ?? 'manual', created_by: auth.user.id,
    } as never);
    if (error) console.warn('[VaulTex] tasks.create:', error.message);
    return !error;
  },

  async setStatus(id, status) {
    const patch: Record<string, unknown> = { status, completed_at: status === 'done' ? new Date().toISOString() : null };
    const { error } = await supabase.from('tasks').update(patch as never).eq('id', id);
    if (error) console.warn('[VaulTex] tasks.setStatus:', error.message);
    return !error;
  },

  async claim(id, profileId) {
    const { error } = await supabase.from('tasks')
      .update({ assignee_id: profileId, status: 'in_progress' } as never)
      .eq('id', id).is('assignee_id', null); // nadie roba tareas ya reclamadas
    if (error) console.warn('[VaulTex] tasks.claim:', error.message);
    return !error;
  },
};

// ─── Implementación demo (fixtures) ─────────────────────────────────────────

const DEMO_TASKS: Task[] = [
  { id: 't1', title: 'Revisar el W-2 rechazado de María Santos', status: 'open', priority: 'high',
    queueRole: 'tax_preparer', dueDate: '2026-09-04', clientId: 'c1', clientName: 'María Santos',
    source: 'workflow', createdAt: '2026-09-03T10:00:00Z' },
  { id: 't2', title: 'Llamar a Andrés Rivera por su cita', status: 'open', priority: 'normal',
    queueRole: 'support', dueDate: '2026-09-05', clientId: 'c2', clientName: 'Andrés Rivera',
    source: 'manual', createdAt: '2026-09-03T09:00:00Z' },
  { id: 't3', title: 'Preparar paquete de firma de Núñez LLC', status: 'in_progress', priority: 'urgent',
    assigneeId: 'demo-user', assigneeName: 'G. Cassimmit', dueDate: '2026-09-02',
    clientId: 'c4', clientName: 'Núñez LLC', source: 'manual', createdAt: '2026-09-01T15:00:00Z',
    escalatedAt: '2026-09-05T11:00:00Z' },
];
let demoTaskSeq = 3;

const fixtureTasks: TasksService = {
  list: () => Promise.resolve(JSON.parse(JSON.stringify(DEMO_TASKS)) as Task[]),
  create: (input) => {
    DEMO_TASKS.unshift({
      id: `t${++demoTaskSeq}`, title: input.title.trim(), details: input.details ?? null,
      status: 'open', priority: input.priority, queueRole: input.queueRole ?? null,
      assigneeId: input.assigneeId ?? null, dueDate: input.dueDate ?? null,
      clientId: input.clientId ?? null, clientName: null,
      source: input.source ?? 'manual', createdAt: new Date().toISOString(),
    });
    return Promise.resolve(true);
  },
  setStatus: (id, status) => {
    const t = DEMO_TASKS.find((x) => x.id === id);
    if (t) t.status = status;
    return Promise.resolve(true);
  },
  claim: (id, profileId) => {
    const t = DEMO_TASKS.find((x) => x.id === id);
    if (!t || t.assigneeId) return Promise.resolve(false);
    t.assigneeId = profileId;
    t.assigneeName = 'Tú';
    t.status = 'in_progress';
    return Promise.resolve(true);
  },
};

export const tasksService: TasksService = hasBackend ? realTasks : fixtureTasks;
